import "./PaymentTerms.scss";
import React from "react";
import {observer} from "mobx-react-lite";
import {Box, Button, Menu, MenuItem, ToggleButton, ToggleButtonGroup, Typography} from "@mui/material";
import {
    AccountBalanceOutlined,
    BoltOutlined,
    CallSplitOutlined,
    ExpandMoreOutlined,
    FileDownloadOutlined,
    PaymentsOutlined,
    SavingsOutlined,
    ScheduleOutlined,
    StackedBarChartOutlined,
    TaskAltOutlined,
    TimelineOutlined,
    TrendingDownOutlined,
} from "@mui/icons-material";
import {useGridApiRef} from "@mui/x-data-grid-premium";
import {MainPageFacade} from "~/components/main/page/MainPageFacade.tsx";
import {CARD_CONFIG} from "~/components/dashboard/DashboardCardConfigs.tsx";
import type {SitKpiPill} from "~/sit/components/SitKpiHeader/SitKpiHeader.tsx";
import {SitKpiHeader} from "~/sit/components/SitKpiHeader/SitKpiHeader.tsx";
import type {PtExclusions} from "./paymentTermsUtils.ts";
import {loadExclusions, saveExclusions} from "./paymentTermsUtils.ts";
import {exportPaymentTermsCsv, exportPaymentTermsExcel} from "./paymentTermsExport.ts";
import {PaymentTermsTable} from "./components/PaymentTermsTable.tsx";
import {SupplierTermsDrawer} from "./components/SupplierTermsDrawer.tsx";
import {TermLandscapeView} from "./components/TermLandscapeView.tsx";
import {HarmonisationView} from "./components/HarmonisationView.tsx";
import {TrendView} from "./components/TrendView.tsx";
import {
    daysLabel,
    intOrMissing,
    MISSING,
    moneyOrMissing,
    pctOrMissing,
    periodRange,
    targetDaysPhrase,
    textOrMissing,
} from "./paymentTermsDisplay.ts";
import {usePaymentTermsPageDisplay, usePaymentTermsSuppliersQuery} from "./usePaymentTermsQueries.ts";
import type {PtSupplier} from "~/experiments/pages/payment-terms/PaymentTermsClasses.ts";
import {B} from "~/build.ts";

type PtMode = 'opportunities' | 'landscape' | 'harmonisation' | 'trend';

const MODES: {value: PtMode; label: string; icon: React.ReactNode}[] = [
    {value: 'opportunities', label: 'Opportunities', icon: <AccountBalanceOutlined fontSize="small"/>},
    {value: 'landscape', label: 'Term landscape', icon: <StackedBarChartOutlined fontSize="small"/>},
    {value: 'harmonisation', label: 'Harmonisation', icon: <CallSplitOutlined fontSize="small"/>},
    {value: 'trend', label: 'Trend', icon: <TimelineOutlined fontSize="small"/>},
];

export const PaymentTermsPage: React.FC = observer(() => {
    const apiRef = useGridApiRef();
    const [mode, setMode] = React.useState<PtMode>('opportunities');
    const [selected, setSelected] = React.useState<PtSupplier | null>(null);
    const [exclusions, setExclusions] = React.useState<PtExclusions>(loadExclusions);
    const [exportAnchor, setExportAnchor] = React.useState<HTMLElement | null>(null);
    const {data: suppliers = [], isPending: suppliersLoading} = usePaymentTermsSuppliersQuery();
    const d = usePaymentTermsPageDisplay();

    const updateExclusions = (next: PtExclusions) => {
        setExclusions(next);
        saveExclusions(next);
    };

    // The review workflow removes disqualified suppliers from the opportunity totals.
    const excluded = React.useMemo(() =>
        suppliers.filter(s => exclusions[s.id]), [suppliers, exclusions]);
    const excludedWcMax = excluded.reduce((sum, s) => sum + s.wcOpportunity, 0);
    const excludedWcMin = excluded.reduce((sum, s) => sum + s.quickWinOpportunity, 0);
    const wcMax = d.wcOpportunity == null ? null : d.wcOpportunity - excludedWcMax;
    const wcMin = d.quickWinOpportunity == null ? null : d.quickWinOpportunity - excludedWcMin;
    const wcRange = wcMin == null && wcMax == null
        ? MISSING
        : `${moneyOrMissing(wcMin, d.currency)} – ${moneyOrMissing(wcMax, d.currency)}`;

    const kpis: SitKpiPill[] = [
        {
            tone: 'plain', icon: <PaymentsOutlined/>, label: 'Total spend (period)',
            value: moneyOrMissing(d.totalPositiveSpend, d.currency),
            help: `Total positive third-party spend in scope, ${periodRange(d)}. ` +
                `${pctOrMissing(d.termCoveragePct)} of it has mapped payment terms.`,
        },
        {
            tone: 'ok', icon: <SavingsOutlined/>, label: 'Working capital opportunity',
            value: wcRange,
            help: 'Range of one-time cash release. Minimum: align every entity to the best term each supplier ' +
              `already accepts (quick win). Maximum: extend every below-target supplier to the ${targetDaysPhrase(d.targetTermDays)} ` +
                'policy term (spend × term gap ÷ 365).' +
                (excluded.length > 0 ? ` Adjusted for ${excluded.length} supplier(s) disqualified in review.` : ''),
        },
        {
            tone: 'info', icon: <ScheduleOutlined/>, label: 'Weighted avg payment term',
            value: d.weightedAvgTerm == null ? MISSING : `${d.weightedAvgTerm.toFixed(1)} days`,
            help: `Spend-weighted average of contractual payment terms across all suppliers. Policy target is ${daysLabel(d.targetTermDays)}. ` +
                'See the Trend view for month-by-month progress.',
        },
        {
            tone: 'warn', icon: <TrendingDownOutlined/>, label: 'Spend below target terms',
            value: <>{moneyOrMissing(d.spendBelowTarget, d.currency)} · {pctOrMissing(d.spendBelowTargetPct)}</>,
            help: `Spend where the current payment term is shorter than the ${targetDaysPhrase(d.targetTermDays)} target — ` +
                'the base the working capital opportunity is computed on.',
        },
        {
            tone: 'warn', icon: <BoltOutlined/>, label: 'Immediate payment exposure',
            value: <>{moneyOrMissing(d.immediateExposure, d.currency)} · {pctOrMissing(d.immediateExposurePct)}</>,
            help: 'Spend paid on immediate (0-day) terms — the fastest lever to release cash.',
        },
        {
            tone: 'ok', icon: <TaskAltOutlined/>, label: 'Suppliers meeting policy',
            value: pctOrMissing(d.suppliersMeetingPolicyPct),
            help: `Share of suppliers whose weighted term meets the ${targetDaysPhrase(d.targetTermDays)} policy.`,
        },
    ];

    const modeSelector = (
        <Box className="pt-modes" display="flex" alignItems="center" gap={1.5}>
            <ToggleButtonGroup
                exclusive
                size="small"
                value={mode}
                onChange={(_e, v) => v && setMode(v)}>
                {MODES.map(m => (
                    <ToggleButton key={m.value} value={m.value}>
                        {m.icon}{m.label}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
            <Button
                variant="outlined"
                size="small"
                id="pt-export-button"
                aria-controls={exportAnchor ? 'pt-export-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={exportAnchor ? 'true' : undefined}
                startIcon={<FileDownloadOutlined/>}
                endIcon={<ExpandMoreOutlined/>}
                onClick={e => setExportAnchor(e.currentTarget)}>
                Export
            </Button>
            <Menu
                id="pt-export-menu"
                anchorEl={exportAnchor}
                open={Boolean(exportAnchor)}
                onClose={() => setExportAnchor(null)}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}
                slotProps={{list: {'aria-labelledby': 'pt-export-button'}}}>
                <MenuItem onClick={() => {
                    setExportAnchor(null);
                    void exportPaymentTermsExcel(suppliers, exclusions);
                }}>
                    Download as Excel
                </MenuItem>
                <MenuItem onClick={() => {
                    setExportAnchor(null);
                    exportPaymentTermsCsv(suppliers, exclusions);
                }}>
                    Download as CSV
                </MenuItem>
            </Menu>
        </Box>
    );

  // TODO: The Transactions and Term coverage does not have an API endpoint yet.
  //  Thus we can only show it for SHOWCASE, in a real environment we will hide these until the API is ready.
    const scopeStrip = (
        <Box className="pt-scope-strip">
            <ScopeItem label="Analysis period" value={periodRange(d)} hint={d.periodLabel || undefined}/>
            <ScopeItem label="Parent suppliers" value={intOrMissing(d.supplierCount)}/>
            {B.PAYMENT_TERMS_SHOWCASE && <ScopeItem label="Transactions" value={intOrMissing(d.transactionCount)}/>}
            {B.PAYMENT_TERMS_SHOWCASE && <ScopeItem label="Term coverage" value={pctOrMissing(d.termCoveragePct)}/>}
            <ScopeItem label="Target policy" value={daysLabel(d.targetTermDays)}/>
        </Box>
    );
  const scopeNote = B.PAYMENT_TERMS_SHOWCASE
      ? <Typography className="pt-scope-note" variant="caption">
          {textOrMissing(d.scopeNote)}
      </Typography>
      : null;

    return <MainPageFacade
        title={CARD_CONFIG['payment-terms'].title}
        titleTag={CARD_CONFIG['payment-terms'].tag}
        className="payment-terms-page"
        actionBarEnd={modeSelector}>

        {scopeStrip}

        {mode !== 'harmonisation' && <SitKpiHeader items={kpis}/>}

        {mode === 'opportunities' &&
            <Box className="pt-table-card">
                <Box className="pt-table-card-header">
                    <Typography variant="h2" sx={{fontSize: 18, fontWeight: 700}}>
                        Supplier opportunities ({suppliers.length - excluded.length})
                        {excluded.length > 0 &&
                            <span className="pt-excluded-count"> · {excluded.length} excluded in review</span>}
                    </Typography>
                    <span className="pt-table-card-hint">
                        {intOrMissing(d.longTailCount)} long-tail suppliers
                        ({moneyOrMissing(d.longTailWcOpportunity, d.currency)}) hidden ·
                        Click a row to review, drill in or disqualify.
                    </span>
                </Box>
                <PaymentTermsTable
                    rows={suppliers}
                    apiRef={apiRef}
                    exclusions={exclusions}
                    currency={d.currency}
                    loading={suppliersLoading}
                    onRowClick={setSelected}
                />
            </Box>
        }

        {mode === 'landscape' && <TermLandscapeView/>}
        {mode === 'harmonisation' && <HarmonisationView rows={suppliers} onSelect={setSelected}/>}
        {mode === 'trend' && <TrendView/>}

        {scopeNote}

        <SupplierTermsDrawer
            supplier={selected}
            exclusionReason={selected ? exclusions[selected.id] : undefined}
            onExclude={(s, reason) => updateExclusions({...exclusions, [s.id]: reason})}
            onRequalify={s => {
                const next = {...exclusions};
                delete next[s.id];
                updateExclusions(next);
            }}
            onClose={() => setSelected(null)}
        />
    </MainPageFacade>;
});

const ScopeItem: React.FC<{label: string; value: string; hint?: string}> = ({label, value, hint}) => (
    <Box className="pt-scope-item">
        <span className="pt-scope-item-label">{label}</span>
        <span className="pt-scope-item-value">{value}</span>
        {hint && <span className="pt-scope-item-hint">{hint}</span>}
    </Box>
);
